import { useRef, useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  Ban, CalendarOff, UserRound, Lightbulb, Sparkles, BookOpen,
  Languages, Headphones, Image as ImageIcon, Flame, ArrowRight,
  ChevronLeft, ChevronRight,
} from "lucide-react";

const ICONS = [Ban, CalendarOff, UserRound, Lightbulb, Sparkles, BookOpen, Languages, Headphones, ImageIcon, Flame];

const IMAGES = [
  "https://images.unsplash.com/photo-1626348448069-9b156d20faeb?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  "https://images.unsplash.com/photo-1684827844157-5a9085408044?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  "https://images.unsplash.com/photo-1486679679458-629f321a617c?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  "https://images.unsplash.com/photo-1535868463750-c78d9543614f?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  "https://images.unsplash.com/photo-1628498188904-036f5e25e93e?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  "https://images.unsplash.com/photo-1579017308347-e53e0d2fc5e9?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  "https://images.unsplash.com/photo-1619392553201-3d9ab3169271?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  "https://images.unsplash.com/photo-1748239994589-95ec583d9e8f?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  "https://images.unsplash.com/photo-1606819717115-9159c900370b?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  "https://images.unsplash.com/photo-1552394459-917cbbffbc84?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
];

function SlideCard({ arg, index }) {
  const Icon = ICONS[index] || Sparkles;
  return (
    <motion.article
      data-card
      data-testid={`arg-card-${index}`}
      initial={{ opacity: 0, scale: 0.96 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className="snap-center shrink-0 w-[80vw] sm:w-[360px] h-[440px] relative overflow-hidden rounded-3xl border border-primary/25 group"
      style={{ boxShadow: "0 18px 50px rgba(0,0,0,0.55)" }}
    >
      <img
        src={IMAGES[index]}
        alt=""
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover scale-105 group-hover:scale-110 transition-transform duration-[1200ms] ease-out"
      />
      <div
        className="absolute inset-0"
        style={{ background: "linear-gradient(180deg, hsl(24 40% 6% / 0.10) 0%, hsl(24 45% 6% / 0.55) 55%, hsl(24 55% 4% / 0.94) 100%)" }}
      />
      <div
        className="absolute inset-0 opacity-40"
        style={{ background: "radial-gradient(circle at 80% 8%, hsl(32 95% 55% / 0.40), transparent 55%)" }}
      />

      <div className="relative z-10 h-full flex flex-col justify-between p-7">
        <div className="flex items-start justify-between">
          <span className="font-display text-gradient-orange text-5xl leading-none tabular-nums drop-shadow">
            {String(index + 1).padStart(2, "0")}
          </span>
          <span className="w-11 h-11 rounded-full bg-black/35 backdrop-blur-sm border border-primary/30 flex items-center justify-center">
            <Icon className="text-primary" size={22} strokeWidth={1.5} />
          </span>
        </div>
        <div>
          <h3 className="font-display text-2xl text-white leading-tight mb-3 drop-shadow-lg">{arg.t}</h3>
          <p className="text-white/75 text-sm leading-relaxed drop-shadow">{arg.d}</p>
        </div>
      </div>
    </motion.article>
  );
}

export function Eventail() {
  const { t } = useTranslation();
  const args = t("landing.eventail.args", { returnObjects: true }) || [];
  const list = Array.isArray(args) ? args : [];
  const trackRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  const update = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setProgress(max > 0 ? el.scrollLeft / max : 0);
    setAtStart(el.scrollLeft <= 4);
    setAtEnd(el.scrollLeft >= max - 4);
  }, []);

  useEffect(() => {
    update();
    const el = trackRef.current;
    if (!el) return;
    el.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => { el.removeEventListener("scroll", update); window.removeEventListener("resize", update); };
  }, [update]);

  const scrollByCards = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    const card = el.querySelector("[data-card]");
    const w = card ? card.offsetWidth + 20 : 380;
    el.scrollBy({ left: dir * w, behavior: "smooth" });
  };

  // gentle auto-advance (loops), paused on hover/touch
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (paused) return;
    const iv = setInterval(() => {
      const el = trackRef.current;
      if (!el) return;
      const max = el.scrollWidth - el.clientWidth;
      const card = el.querySelector("[data-card]");
      const w = card ? card.offsetWidth + 20 : 380;
      if (el.scrollLeft >= max - 4) {
        el.scrollTo({ left: 0, behavior: "smooth" });
      } else {
        el.scrollBy({ left: w, behavior: "smooth" });
      }
    }, 5000);
    return () => clearInterval(iv);
  }, [paused]);

  return (
    <section className="relative overflow-hidden px-6 py-14 sm:py-24" data-testid="eventail-section">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <p className="text-primary/80 tracking-[0.25em] uppercase text-xs mb-4">{t("landing.eventail.eyebrow")}</p>
          <h2 className="font-display text-gradient-orange text-4xl sm:text-5xl italic">{t("landing.eventail.title")}</h2>
        </div>

        <div className="relative"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onTouchStart={() => setPaused(true)}
        >
          {/* arrows (desktop) */}
          <button
            data-testid="eventail-prev"
            onClick={() => scrollByCards(-1)}
            disabled={atStart}
            aria-label="Précédent"
            className="hidden md:flex absolute -left-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-background/70 backdrop-blur-md border border-white/15 items-center justify-center text-foreground/80 hover:text-primary hover:border-primary/50 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronLeft size={22} />
          </button>
          <button
            data-testid="eventail-next"
            onClick={() => scrollByCards(1)}
            disabled={atEnd}
            aria-label="Suivant"
            className="hidden md:flex absolute -right-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full bg-background/70 backdrop-blur-md border border-white/15 items-center justify-center text-foreground/80 hover:text-primary hover:border-primary/50 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronRight size={22} />
          </button>

          {/* edge fades */}
          <div className="hidden md:block pointer-events-none absolute left-0 top-0 bottom-0 w-16 z-10 bg-gradient-to-r from-background to-transparent" />
          <div className="hidden md:block pointer-events-none absolute right-0 top-0 bottom-0 w-16 z-10 bg-gradient-to-l from-background to-transparent" />

          {/* track */}
          <div
            ref={trackRef}
            data-testid="eventail-carousel"
            className="flex gap-5 overflow-x-auto snap-x snap-mandatory pb-4 [&::-webkit-scrollbar]:hidden"
            style={{ scrollbarWidth: "none", scrollPadding: "0 24px" }}
          >
            {list.map((arg, i) => (
              <SlideCard key={i} arg={arg} index={i} />
            ))}
          </div>
        </div>

        {/* progress bar */}
        <div className="mt-6 mx-auto max-w-xs h-1 rounded-full bg-white/10 overflow-hidden" data-testid="eventail-progress">
          <motion.div
            className="h-full rounded-full bg-gradient-to-r from-primary/70 to-primary"
            animate={{ width: `${Math.max(8, progress * 100)}%` }}
            transition={{ duration: 0.2 }}
          />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}
          className="flex flex-col items-center gap-4 mt-12"
        >
          <Link
            to="/entrer"
            data-testid="eventail-join-btn"
            className="btn-frost-orange inline-flex items-center gap-2 px-10 py-4 rounded-full font-semibold text-base"
          >
            {t("landing.eventail.join")} <ArrowRight size={18} />
          </Link>
          <Link
            to="/tarifs"
            data-testid="eventail-tarifs-link"
            className="text-sm text-foreground/50 hover:text-primary transition-colors"
          >
            {t("landing.eventail.tarifs")}
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
